import * as THREE from 'three';
import { createCharacter } from './character.mjs';
import { loadActorAssets } from './actor-assets.mjs';
import { PlayerHealth } from './health.mjs';
import { sweepInfiniteWalls } from './walls.mjs';
import { createDynamicTerrain } from './dynamic-terrain.mjs';
import { createSourceBlockController } from './deflection.mjs';
import { JediCombo, sampledSaberTarget } from './combat.mjs';
import { parseTerrain, flattenStaticTerrain } from './terrain.mjs';
import { createSaber } from './weapon.mjs';
import { sourceCharacterAbilities } from './party.mjs';

// Integration harness. Timings, poses and strike selection come from the
// reconstructed source and owned assets. Camera, collision response, spawning
// and this test encounter are provisional; level scripts/AI are not emulated.
export function createPlaytest({ scene, camera, orbit, canvas, onError, onCameraMode }) {
  const world = new THREE.Group(); world.scale.x = -1; scene.add(world);
  const keys = new Set(), ray = new THREE.Raycaster();
  let player = null, enemies = [], partyActors = [], collider = null, terrainData = null;
  let rawTerrain = null, dynamicTerrain = null, supportingPlatform = null;
  let actorAssetCache = new Map(), loadGeneration = 0, gameHooks = null;
  let interaction = null;
  let blockPressed = false;
  let active = false, placing = false, preview = null, animationTime = 0, currentClip = null, currentFrame = 1, transition = null;
  let saber = null, weaponDrawn = false;
  let spawn = { position: [-1, 0, 0], heading: Math.PI / 2 };
  let velocity = new THREE.Vector3(), jumps = 0, grounded = true, landing = 0, landedFromDouble = false;
  const report = { state: 'empty', active: false, strikes: 0, hits: 0, defeats: 0, frame: 1, clip: null, events: [], limitations: [
    'Test encounter; original level scripts, enemy AI, progression and cinematics are not connected.',
    'Static TER collision only; dynamic platforms, special-object states and infinite-wall response remain incomplete.',
    'Grounded Jedi combo subset; blocking, bolt deflection, Force interactions and airborne combat remain incomplete.',
    'Movement/camera response is provisional; original-runtime behavior comparison remains required.',
  ] };
  const status = document.querySelector('#character-status'), select = document.querySelector('#animation');
  const centerOf = actor => ({ x: actor.root.position.x, y: actor.root.position.y + (actor.config.values.miny + actor.config.values.maxy) / 2, z: actor.root.position.z });
  const worldNormal = hit => hit.face.normal.clone().applyMatrix3(new THREE.Matrix3().getNormalMatrix(hit.object.matrixWorld)).normalize();
  function poseActor(actor, clip, frame, blend = null) { actor.pose(clip, frame, blend); actor.currentClip = clip; actor.currentFrame = frame; }
  function floorAt(x, y, z, distance = 1000) {
    if (!collider && !dynamicTerrain) return null;
    ray.set(new THREE.Vector3(x, y, z), new THREE.Vector3(0, -1, 0)); ray.far = distance;
    return ray.intersectObjects([...(collider ? [collider] : []), ...(dynamicTerrain?.activeMeshes ?? [])], false).find(hit => Math.abs(worldNormal(hit).y) > 0.45) ?? null;
  }
  function resetEnemyPositions() {
    if (!player) return;
    const p = player.root.position, heading = player.root.rotation.y;
    enemies.forEach((enemy, i) => {
      enemy.dead = false; enemy.root.visible = true; enemy.hp = enemy.config.values.hit_points;
      if (enemy.authoredSpawn) {
        enemy.deathTime = 0; enemy.root.position.fromArray(enemy.authoredSpawn.position); enemy.root.rotation.y = enemy.authoredSpawn.heading;
        return;
      }
      const side = i % 2 ? 0.25 : -0.25, forward = 0.8 + Math.floor(i / 2) * 0.6;
      enemy.deathTime = 0; enemy.root.position.set(p.x + Math.sin(heading) * forward + Math.cos(heading) * side,
        p.y, p.z + Math.cos(heading) * forward - Math.sin(heading) * side);
      const floor = floorAt(enemy.root.position.x, p.y + 0.2, enemy.root.position.z, 0.5);
      if (floor) enemy.root.position.y = floor.point.y;
      enemy.root.rotation.y = heading + Math.PI;
    });
    report.defeats = 0;
  }
  function reset() {
    if (!player) return;
    player.root.position.fromArray(spawn.position); player.root.rotation.y = spawn.heading;
    const floor = floorAt(spawn.position[0], spawn.position[1] + 0.3, spawn.position[2], 20); if (floor) player.root.position.y = floor.point.y;
    velocity.set(0, 0, 0); jumps = 0; grounded = true; landing = 0; landedFromDouble = false; player.combo.reset();
    player.health?.reset(); player.dead = false; player.root.visible = true;
    player.blockController?.reset(); blockPressed = false;
    supportingPlatform = null;
    interaction = null;
    report.strikes = report.hits = report.defeats = 0; report.events = []; preview = null; weaponDrawn = false;
    currentClip = null; currentFrame = 1; animationTime = 0; transition = null;
    report.position = player.root.position.toArray(); report.grounded = true; report.clip = 'idle'; report.frame = 1; report.combo = player.combo.state;
    resetEnemyPositions(); focus();
  }
  function focus() {
    if (!player) return;
    const p = player.root.position, target = new THREE.Vector3(-p.x, p.y + 0.24, p.z);
    const h = player.root.rotation.y;
    orbit.target.copy(target); camera.position.copy(target).add(new THREE.Vector3(Math.sin(h) * 1.1 - Math.cos(h) * 0.5, 0.65, -Math.cos(h) * 1.1 - Math.sin(h) * 0.5));
    camera.near = 0.01; camera.far = 10000; camera.updateProjectionMatrix(); camera.lookAt(target); orbit.update();
  }
  function setActive(value) {
    active = value && !!player; placing = false; keys.clear(); orbit.enabled = true;
    report.active = active; document.querySelector('#play').textContent = active ? 'Pause controls' : 'Playtest controls';
    gameHooks?.onActive?.(active);
    onCameraMode(active);
    if (active) { preview = null; canvas.focus(); focus(); }
  }
  function clear() {
    loadGeneration++; actorAssetCache = new Map(); gameHooks = null;
    setActive(false);
    for (const actor of partyActors) { actor.weaponVisual?.dispose(); actor.dispose(); }
    partyActors = []; saber = null; player = null;
    enemies.forEach(enemy => { enemy.weaponVisual?.dispose(); enemy.dispose(); }); enemies = [];
    collider?.geometry.dispose(); collider?.material.dispose(); collider = null; terrainData = null;
    dynamicTerrain?.dispose(); dynamicTerrain = null; rawTerrain = null; supportingPlatform = null;
    interaction = null;
    preview = null; currentClip = null; transition = null; report.state = 'empty';
    spawn = { position: [-1, 0, 0], heading: Math.PI / 2 };
    for (const id of ['play', 'focus-character', 'reset-encounter', 'animation', 'place-character']) document.querySelector(`#${id}`).disabled = true;
    select.replaceChildren(); status.textContent = 'Character assets not loaded.';
  }
  function strike(event) {
    report.strikes++;
    const targets = enemies.map(actor => ({ actor, position: centerOf(actor), radius: actor.config.values.radius,
      yRadius: (actor.config.values.maxy - actor.config.values.miny) / 2, dead: actor.dead, eligible: actor.eligibleDamage !== false }));
    targets.push(...(gameHooks?.getStrikeTargets?.() ?? []));
    const target = sampledSaberTarget({ position: centerOf(player), heading: player.root.rotation.y }, targets);
    const record = { action: event.action, frame: event.frame, damage: event.damage, hit: !!target };
    if (target?.actor) {
      target.actor.hp = Math.max(0, target.actor.hp - event.damage); report.hits++;
      if (!target.actor.hp) { target.actor.dead = true; target.actor.deathTime = 0; report.defeats++; }
      gameHooks?.onEnemyHit?.(target.actor, event);
    } else if (target) {
      if (gameHooks?.onWorldHit?.(target, event)) report.hits++;
    }
    report.events.push(record); if (report.events.length > 20) report.events.shift();
    gameHooks?.onStrike?.(record);
  }
  async function load(files, { testEncounter = true, characterName = 'quigonjinn' } = {}) {
    clear(); report.state = 'loading'; status.textContent = 'Reading original character assets…';
    const generation = loadGeneration;
    const list = [...files];
    const basename = file => file.name.toLowerCase();
    const find = name => list.find(file => basename(file) === name.toLowerCase());
    const assetsFor = name => loadActorAssets(name, list, { player: name.toLowerCase() === characterName.toLowerCase() });
    const owned = [];
    try {
      const assets = await assetsFor(characterName);
      const nextPlayer = createCharacter(assets.buffer, assets.config, assets.animations); owned.push(nextPlayer);
      nextPlayer.health = new PlayerHealth(assets.config.values.hit_points);
      nextPlayer.playerControlled = true; nextPlayer.blockController = createSourceBlockController(nextPlayer);
      nextPlayer.pose('idle', 1);
      const things = find('THINGS_PC.GSC');
      nextPlayer.name = characterName.toLowerCase(); nextPlayer.abilities = sourceCharacterAbilities(assets.config);
      const nextSaber = things && nextPlayer.abilities.jedi ? createSaber(await things.arrayBuffer(), nextPlayer) : null;
      nextPlayer.weaponVisual = nextSaber;
      if (nextSaber) owned.push(nextSaber);
      nextPlayer.combo = new JediCombo(assets.config, { clipEndFrame: clip => assets.animations.get(clip)?.endFrame, onStrike: strike });
      const nextEnemies = [];
      if (testEncounter && find('BATTLEDROID_PC.GHG')) {
        const enemyAssets = await assetsFor('BATTLEDROID');
        for (let i = 0; i < 4; i++) { const enemy = createCharacter(enemyAssets.buffer, enemyAssets.config, enemyAssets.animations); owned.push(enemy); enemy.pose('idle', 1); nextEnemies.push(enemy); }
      }
      const terrainFiles = list.filter(file => /\.ter$/i.test(file.name));
      if (terrainFiles.length > 1) throw new Error('Choose a playtest folder with one terrain file.');
      const ter = terrainFiles[0];
      const nextRawTerrain = ter ? parseTerrain(await ter.arrayBuffer()) : null;
      const nextTerrain = nextRawTerrain ? flattenStaticTerrain(nextRawTerrain) : null;
      if (generation !== loadGeneration) { owned.forEach(actor => actor.dispose()); return; }
      saber?.dispose(); player?.dispose(); enemies.forEach(enemy => enemy.dispose());
      collider?.geometry.dispose(); collider?.material.dispose(); collider = null;
      player = nextPlayer; enemies = nextEnemies; saber = nextSaber; world.add(player.root, ...enemies.map(enemy => enemy.root));
      partyActors = [player];
      terrainData = nextTerrain;
      rawTerrain = nextRawTerrain;
      if (terrainData) {
        const geometry = new THREE.BufferGeometry(); geometry.setAttribute('position', new THREE.BufferAttribute(terrainData.positions, 3));
        geometry.computeVertexNormals(); geometry.computeBoundingBox(); geometry.computeBoundingSphere();
        collider = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ side: THREE.DoubleSide })); collider.updateMatrixWorld(true);
      }
      select.replaceChildren();
      for (const [clip] of player.animations) { const option = document.createElement('option'); option.value = clip; option.textContent = clip; select.append(option); }
      select.value = 'idle';
      for (const id of ['play', 'focus-character', 'reset-encounter', 'animation', 'place-character']) document.querySelector(`#${id}`).disabled = false;
      document.querySelector('#place-character').disabled = !collider;
      report.state = 'ready'; report.character = player.summary; report.animationClips = player.animations.size;
      report.enemyModel = enemies[0]?.summary ?? null; report.terrainTriangles = terrainData?.triangles.length ?? 0;
      report.weapon = saber?.summary ?? null;
      reset(); update(0);
    } catch (e) { owned.forEach(actor => actor.dispose()); if (generation !== loadGeneration) return; clear(); report.state = 'error'; status.textContent = 'Character loading failed.'; onError(e); }
  }
  function move(dt) {
    const cfg = player.config.values, p = player.root.position;
    dynamicTerrain?.update();
    if (supportingPlatform && supportingPlatform.mesh.userData.collisionActive) {
      const beforeCarry = p.clone(); p.copy(supportingPlatform.localPosition).applyMatrix4(supportingPlatform.mesh.matrix);
      const carry = p.clone().sub(beforeCarry); carry.x *= -1; camera.position.add(carry); orbit.target.add(carry);
    }
    const previous = p.clone(), combo = player.combo.state;
    const forward = camera.getWorldDirection(new THREE.Vector3()); forward.y = 0; forward.normalize();
    const right = new THREE.Vector3().crossVectors(forward, camera.up).normalize();
    const desired = forward.multiplyScalar(Number(keys.has('KeyW')) - Number(keys.has('KeyS')))
      .addScaledVector(right, Number(keys.has('KeyD')) - Number(keys.has('KeyA')));
    desired.x *= -1;
    if (desired.lengthSq()) desired.normalize();
    if (interaction || player.blockController.state.name !== 'none') { desired.set(0, 0, 0); velocity.x = velocity.z = 0; }
    let speed = keys.has('ShiftLeft') || keys.has('ShiftRight') ? cfg.walk_speed : cfg.run_speed;
    if (combo.active) { speed = player.config.actions.get(combo.action).actionSpeed; desired.set(Math.sin(player.root.rotation.y), 0, Math.cos(player.root.rotation.y)); }
    const factor = Math.min(1, cfg.acceleration * dt);
    velocity.x += (desired.x * speed - velocity.x) * factor; velocity.z += (desired.z * speed - velocity.z) * factor;
    if (interaction?.kind === 'panel') {
      velocity.x = interaction.targetVelocity?.[0] ?? 0; velocity.z = interaction.targetVelocity?.[2] ?? 0;
      player.root.rotation.y = interaction.heading;
    }
    const horizontal = new THREE.Vector3(velocity.x * dt, 0, velocity.z * dt);
    if ((collider || dynamicTerrain) && horizontal.lengthSq()) {
      // A small swept radius prevents walking through static triangles; original
      // capsule, step/slope/platform response is still a separate parity gate.
      ray.set(p.clone().add(new THREE.Vector3(0, cfg.maxy * 0.5, 0)), horizontal.clone().normalize());
      ray.far = horizontal.length() + cfg.radius;
      if (ray.intersectObjects([...(collider ? [collider] : []), ...(dynamicTerrain?.activeMeshes ?? [])], false).some(hit => Math.abs(worldNormal(hit).y) < 0.45)) horizontal.set(0, 0, 0);
    }
    if (terrainData?.infiniteWalls.length && horizontal.lengthSq()) {
      const wall = sweepInfiniteWalls(terrainData.infiniteWalls, p, horizontal, cfg.radius);
      if (wall) { horizontal.multiplyScalar(Math.max(0, wall.time - 0.00001)); report.wallContact = wall; }
      else report.wallContact = null;
    }
    p.add(horizontal);
    if (!combo.active && desired.lengthSq()) player.root.rotation.y = Math.atan2(desired.x, desired.z);
    const previousY = p.y;
    if (!grounded) { velocity.y += cfg.air_gravity * dt; p.y += velocity.y * dt; }
    const floor = floorAt(p.x, Math.max(previousY, p.y) + 0.06, p.z, cfg.maxy + 0.15);
    if (collider) {
      if (floor && p.y <= floor.point.y + 0.02 && velocity.y <= 0) {
        if (!grounded) startLanding();
        grounded = true; jumps = 0; velocity.y = 0; p.y = floor.point.y;
        supportingPlatform = floor.object.userData.platformIndex === undefined ? null : { mesh: floor.object, localPosition: p.clone().applyMatrix4(floor.object.matrix.clone().invert()) };
      } else if (grounded) { grounded = false; jumps = 1; }
    } else if (p.y <= 0) { if (!grounded) startLanding(); p.y = 0; grounded = true; jumps = 0; velocity.y = 0; }
    if (p.y < -100) reset();
    if (!grounded) supportingPlatform = null;
    const delta = new THREE.Vector3(-(p.x - previous.x), p.y - previous.y, p.z - previous.z);
    camera.position.add(delta); orbit.target.add(delta);
    report.position = p.toArray(); report.grounded = grounded;
    return desired.lengthSq() > 0 && speed > 0;
  }
  function startLanding() {
    landedFromDouble = jumps > 1;
    const clip = landedFromDouble ? 'land2' : 'land', info = player.config.animations.get(clip), animation = player.animations.get(clip);
    landing = animation ? Math.max(0, (animation.endFrame - 1) / (info?.playbackRate ?? 30) - (info?.blendOut ?? 0)) : 0;
  }
  function update(dt) {
    if (!player || report.state !== 'ready') return;
    let moving = false;
    if (active) {
      player.health.update(dt);
      player.blockController.tick(dt, { pressed: blockPressed, held: keys.has('KeyL'), grounded, weaponDrawn,
        allowStart: !interaction && !player.combo.state.active && !player.dead, bolts: gameHooks?.incomingBolts?.() ?? [],
        collision: { position: Object.values(centerOf(player)), radius: player.config.values.radius, halfHeight: (player.config.values.maxy - player.config.values.miny) / 2 } });
      blockPressed = false;
      if (!player.dead) { player.combo.update(dt); moving = move(dt); } gameHooks?.update?.(dt);
    }
    const combo = player.combo.state;
    const interactionClip = interaction ? player.config.actions.get(interaction.kind === 'force' ? 'force1' : interaction.kind === 'panel' ? interaction.action : 'build')?.clip : null;
    let clip = preview || (player.dead ? 'splat' : interactionClip || player.blockController.state.clip || (combo.active ? combo.clip : !grounded ? (velocity.y > 0 ? (jumps > 1 ? 'jump2' : 'jump') : 'fall') : landing > 0 ? (landedFromDouble ? 'land2' : 'land') : moving ? (keys.has('ShiftLeft') || keys.has('ShiftRight') ? 'walk' : 'run') : weaponDrawn ? 'sabreidle' : 'idle'));
    if (!player.animations.has(clip)) clip = 'idle';
    const info = player.config.animations.get(clip), animation = player.animations.get(clip);
    if (clip !== currentClip) {
      transition = currentClip ? { clip: currentClip, frame: currentFrame, elapsed: 0, duration: info?.blendIn ?? 0.2 } : null;
      currentClip = clip; animationTime = 0;
    }
    animationTime += dt;
    const elapsed = animationTime * (info?.playbackRate ?? 30), duration = animation.endFrame - 1;
    currentFrame = combo.active && !preview ? combo.frame : 1 + (!player.dead && info?.cycle !== false ? elapsed % duration : Math.min(elapsed, duration));
    let blend = null;
    if (transition && transition.duration > 0 && transition.elapsed < transition.duration) {
      transition.elapsed += dt;
      const previous = player.animations.get(transition.clip), previousInfo = player.config.animations.get(transition.clip);
      const advanced = transition.frame - 1 + transition.elapsed * (previousInfo?.playbackRate ?? 30);
      const frame = 1 + (previousInfo?.cycle !== false ? advanced % (previous.endFrame - 1) : Math.min(advanced, previous.endFrame - 1));
      blend = { clip: transition.clip, frame, alpha: Math.min(1, transition.elapsed / transition.duration) };
    }
    poseActor(player, clip, currentFrame, blend);
    saber?.update(weaponDrawn || /sabre|combo|lunge|slam|block|swipe/.test(clip));
    landing = Math.max(0, landing - dt);
    for (const enemy of [...enemies, ...partyActors.filter(actor => actor !== player)]) {
      enemy.drawn = false;
      const clip = enemy.dead ? 'splat' : enemy.aiFireClip ?? (enemy.aiClip && enemy.animations.has(enemy.aiClip) ? enemy.aiClip : 'idle'), animation = enemy.animations.get(clip);
      if (!animation) continue;
      enemy.deathTime += dt;
      const frame = enemy.aiFireClip && Number.isFinite(enemy.aiFrame) && !enemy.dead ? enemy.aiFrame : 1 + (enemy.dead ? Math.min(enemy.deathTime * 30, animation.endFrame - 1) : (performance.now() / 1000 * 30) % (animation.endFrame - 1));
      poseActor(enemy, clip, frame);
      enemy.weaponVisual?.update(!enemy.dead);
      if (enemy.dead && frame >= animation.endFrame) enemy.root.visible = false;
    }
    report.frame = currentFrame; report.clip = clip; report.combo = combo;
    report.health = player.health.current; report.maximumHealth = player.health.maximum;
    status.textContent = `${player.animations.size} original clips · ${player.parsed.joints.length} joints\n${clip} · frame ${currentFrame.toFixed(2)}\nHealth ${report.health}/${report.maximumHealth} · ${report.hits} hits · ${report.defeats}/${enemies.length} droids defeated`;
  }
  document.querySelector('#play').addEventListener('click', () => setActive(!active));
  document.querySelector('#focus-character').addEventListener('click', focus);
  document.querySelector('#reset-encounter').addEventListener('click', resetEnemyPositions);
  select.addEventListener('change', () => { setActive(false); preview = select.value; });
  document.querySelector('#place-character').addEventListener('click', () => { setActive(false); placing = true; status.textContent = 'Click a floor to place Qui-Gon and the test droids.'; });
  canvas.addEventListener('pointerdown', e => {
    if (!placing || !collider) return;
    const rect = canvas.getBoundingClientRect();
    ray.setFromCamera(new THREE.Vector2((e.clientX - rect.left) / rect.width * 2 - 1, -(e.clientY - rect.top) / rect.height * 2 + 1), camera);
    // Collider is in native coordinates; mirror the world camera ray back.
    ray.ray.origin.x *= -1; ray.ray.direction.x *= -1; ray.far = 10000;
    const hit = ray.intersectObject(collider, false).find(hit => Math.abs(hit.face.normal.y) > 0.45);
    if (hit) { player.root.position.copy(hit.point); velocity.set(0, 0, 0); grounded = true; jumps = 0; resetEnemyPositions(); placing = false; focus(); }
  });
  addEventListener('keydown', e => {
    if (!active || /INPUT|SELECT|TEXTAREA/.test(e.target.tagName)) return;
    if (['Space', 'KeyW', 'KeyA', 'KeyS', 'KeyD', 'KeyJ'].includes(e.code)) e.preventDefault();
    if (e.code === 'Escape') { setActive(false); return; }
    keys.add(e.code);
    if (e.repeat) return;
    if (e.code === 'KeyL' && !player.dead) blockPressed = true;
    if (e.code === 'KeyU') { gameHooks?.onTag?.(); return; }
    if (e.code === 'KeyJ' && player.abilities.melee && grounded && !player.dead && !interaction && player.blockController.state.name === 'none') { preview = null; weaponDrawn = true; player.combo.press(); }
    if (e.code === 'Space' && player.abilities.jump && jumps < (player.abilities.doubleJump ? 2 : 1) && !player.combo.active && !player.dead && !interaction && player.blockController.state.name === 'none') { velocity.y = jumps ? player.config.values.jump_2_speed : player.config.values.jump_speed; jumps++; grounded = false; }
  });
  addEventListener('keyup', e => keys.delete(e.code));
  addEventListener('blur', () => { keys.clear(); if (active) setActive(false); });
  return { load, update, clear, setActive, focus, reset, report, world,
    async addPartyMember(name, files, { position, heading = 0, thingsBuffer } = {}) {
      if (!player || !Array.isArray(position) || position.length !== 3 || !position.every(Number.isFinite) || !Number.isFinite(heading)) throw new Error('Invalid original party spawn.');
      const generation = loadGeneration, assets = await loadActorAssets(name, files, { player: true });
      if (generation !== loadGeneration) return null;
      const actor = createCharacter(assets.buffer, assets.config, assets.animations);
      actor.name = name.toLowerCase(); actor.abilities = sourceCharacterAbilities(assets.config);
      actor.health = new PlayerHealth(assets.config.values.hit_points); actor.dead = false; actor.deathTime = 0;
      actor.blockController = createSourceBlockController(actor);
      actor.combo = new JediCombo(assets.config, { clipEndFrame: clip => assets.animations.get(clip)?.endFrame, onStrike: strike });
      actor.root.position.fromArray(position); actor.root.rotation.y = heading; actor.pose('idle', 1);
      if (thingsBuffer && actor.abilities.jedi) actor.weaponVisual = createSaber(thingsBuffer, actor);
      partyActors.push(actor); world.add(actor.root); return actor;
    },
    controlActor(actor) {
      if (!partyActors.includes(actor) || actor.dead) throw new Error('Unavailable original party actor.');
      player = actor; saber = actor.weaponVisual ?? null;
      keys.clear(); velocity.set(0, 0, 0); grounded = false; jumps = 0; interaction = null; supportingPlatform = null;
      player.combo.reset(); player.blockController.reset(); blockPressed = false; weaponDrawn = false;
      currentClip = preview = transition = null; animationTime = 0; currentFrame = 1;
      select.replaceChildren(); for (const [clip] of actor.animations) { const option = document.createElement('option'); option.value = clip; option.textContent = clip; select.append(option); }
      select.value = 'idle'; report.character = actor.summary; report.animationClips = actor.animations.size; focus();
    },
    get partyActors() { return partyActors; },
    setGameHooks(hooks) { gameHooks = hooks; },
    setInteraction(value) {
      if (value && (!['force', 'build', 'panel'].includes(value.kind) || !Number.isFinite(value.heading))) throw new Error('Invalid player interaction.');
      if (value && player && interaction?.id !== value.id) { player.combo.reset(); velocity.x = velocity.z = 0; player.root.rotation.y = value.heading; }
      interaction = value; report.interaction = value ? { id: value.id, kind: value.kind } : null;
    },
    get controls() { return { active, grounded, interacting: keys.has('KeyK'), blocking: keys.has('KeyL'), attacking: player?.combo.state.active ?? false }; },
    get supportingPlatformId() { return supportingPlatform?.mesh.userData.terrainGroupIndex ?? null; },
    hasLineOfSight(from, to, targetRadius = 0) {
      const a = Array.isArray(from) ? new THREE.Vector3(...from) : new THREE.Vector3(from.x, from.y, from.z);
      const b = Array.isArray(to) ? new THREE.Vector3(...to) : new THREE.Vector3(to.x, to.y, to.z), delta = b.sub(a), distance = delta.length();
      if (!distance) return true;
      ray.set(a, delta.normalize()); ray.far = Math.max(0, distance - targetRadius);
      return ray.intersectObjects([...(collider ? [collider] : []), ...(dynamicTerrain?.activeMeshes ?? [])], false).length === 0;
    },
    sweepParticle({ position, velocity: motion, duration, radius }) {
      const start = new THREE.Vector3(...position), delta = new THREE.Vector3(...motion).multiplyScalar(duration), distance = delta.length();
      if (!distance) return null;
      const direction = delta.clone().normalize(); ray.set(start, direction); ray.far = distance + radius;
      const hit = ray.intersectObjects([...(collider ? [collider] : []), ...(dynamicTerrain?.activeMeshes ?? [])], false)[0];
      if (!hit) return null;
      const normal = worldNormal(hit); if (normal.dot(direction) > 0) normal.negate();
      const approach = -normal.dot(direction); if (approach < 1e-8) return null;
      const travel = Math.max(0, hit.distance - radius / approach); if (travel > distance) return null;
      return { time: duration * travel / distance, position: start.addScaledVector(direction, travel).toArray(), normal: normal.toArray() };
    },
    floorAt,
    setDynamicTerrainBindings(resolvePlatform) {
      dynamicTerrain?.dispose(); supportingPlatform = null;
      dynamicTerrain = rawTerrain ? createDynamicTerrain(rawTerrain, resolvePlatform) : null;
      report.dynamicTerrain = dynamicTerrain ? { groups: dynamicTerrain.meshes.length, unresolved: dynamicTerrain.unsupported } : null;
    },
    terrainGroupForPlatform(index) { return rawTerrain?.groups.find(group => group.dynamic && group.platformIndex === index)?.index ?? -1; },
    damagePlayer(amount, options) {
      if (!player) return null;
      const event = player.health.hit(amount, options);
      if (event.damage) gameHooks?.onPlayerHit?.(event);
      if (event.killed) { player.dead = true; player.combo.reset(); velocity.set(0, 0, 0); animationTime = 0; gameHooks?.onPlayerDeath?.(); }
      report.health = player.health.current; return event;
    },
    healPlayer(amount = 1) { return player?.health.heal(amount) ?? 0; },
    respawnAt(position, heading, protection = 2.5) {
      if (!player || !Array.isArray(position) || position.length !== 3 || !position.every(Number.isFinite) || !Number.isFinite(heading)) throw new Error('Invalid respawn state.');
      player.root.position.fromArray(position); player.root.rotation.y = heading; player.root.visible = true; player.dead = false;
      player.health.reset(); player.health.spawnProtection = protection; player.combo.reset(); player.blockController.reset();
      velocity.set(0, -0.1, 0); jumps = 0; grounded = false; interaction = null; supportingPlatform = null;
      currentClip = null; animationTime = 0; currentFrame = 1; transition = null; focus();
    },
    damageEnemy(actor, amount) {
      if (!enemies.includes(actor) || actor.dead || actor.eligibleDamage === false) return false;
      actor.hp = Math.max(0, actor.hp - amount); gameHooks?.onEnemyHit?.(actor, { damage: amount });
      if (!actor.hp) { actor.dead = true; actor.deathTime = 0; report.defeats++; }
      return true;
    },
    async addEnemy(name, files, { id, position, heading = 0, ai = null } = {}) {
      if (!player || report.state !== 'ready') throw new Error('Load the player before adding level actors.');
      if (!Array.isArray(position) || position.length !== 3 || !position.every(Number.isFinite) || !Number.isFinite(heading)) throw new Error('Invalid original actor spawn.');
      if (enemies.some(actor => actor.id === id)) throw new Error('Duplicate level actor identity.');
      const generation = loadGeneration, key = name.toLowerCase();
      if (!actorAssetCache.has(key)) actorAssetCache.set(key, loadActorAssets(key, files));
      const assets = await actorAssetCache.get(key);
      if (generation !== loadGeneration) return null;
      const enemy = createCharacter(assets.buffer, assets.config, assets.animations);
      enemy.id = id; enemy.ai = ai; enemy.dead = false; enemy.hp = assets.config.values.hit_points; enemy.deathTime = 0;
      enemy.authoredSpawn = { position: position.slice(), heading };
      enemy.root.position.fromArray(position); enemy.root.rotation.y = heading; enemy.pose('idle', 1);
      enemy.model.traverse(mesh => { if (mesh.isMesh) mesh.onAfterRender = () => { enemy.drawn = true; }; });
      enemies.push(enemy); world.add(enemy.root); report.enemyModel = enemy.summary;
      return enemy;
    },
    removeEnemies() { enemies.forEach(actor => { actor.weaponVisual?.dispose(); actor.dispose(); }); enemies = []; },
    setSpawn(position, heading) {
      if (!Array.isArray(position) || position.length !== 3 || !position.every(Number.isFinite) || !Number.isFinite(heading)) throw new Error('Invalid original spawn.');
      spawn = { position: position.slice(), heading }; reset();
    }, get player() { return player; }, get enemies() { return enemies; } };
}
